// Recipients CRUD. List with Add/Edit modal + delete (blocked if
// any live request exists — server enforces).

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Pencil, Trash2, Phone, MapPin, Users, Loader2, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, SectionHeader, EmptyState } from '../../components/shared/primitives';
import { CUSTOMER_NAV } from '../_navs';
import { useCustomerNav } from '../../lib/pendingAction';
import { catalog, recipients, type Recipient, type RecipientInput } from '../../lib/api';
import { Modal } from '../../components/shared/Modal';
import { Button } from '../../components/stripe-ui/Button';

export function CustomerRecipientsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['me', 'recipients'], queryFn: recipients.list });
  const [editing, setEditing] = useState<Recipient | null>(null);
  const [adding,  setAdding]  = useState(false);

  const inviteMut = useMutation({
    mutationFn: (id: number) => recipients.invite(id),
    onSuccess: (res) => {
      navigator.clipboard?.writeText(res.link).catch(() => {});
      const ch = [res.whatsapp && 'WhatsApp', res.email && 'email'].filter(Boolean).join(' + ');
      toast.success(ch ? `Invite sent via ${ch} · link copied` : 'Invite link copied — share it');
    },
    onError: (e: Error) => toast.error(e.message || 'Could not send invite'),
  });

  const removeMut = useMutation({
    mutationFn: (id: number) => recipients.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['me', 'recipients'] }); toast.success('Recipient removed'); },
    onError: (e: Error) => toast.error(e.message || 'Cannot delete'),
  });

  // Home carries a badge when this role owes an action, so it stays
  // visible from any screen.
  const nav = useCustomerNav(CUSTOMER_NAV);

  return (
    <PortalShell
      nav={nav}
      activeId="recipients"
      title="Recipients"
      subtitle="Family members in Haiti you can buy for."
      actions={<Button onClick={() => setAdding(true)}><Plus className="w-3.5 h-3.5 mr-1" /> Add recipient</Button>}
    >
      <div className="space-y-5">
        {q.isLoading && (
          <Card><div className="text-center py-12 text-[13px] text-[var(--color-muted)]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</div></Card>
        )}
        {!q.isLoading && (q.data?.length ?? 0) === 0 && (
          <Card>
            <EmptyState
              icon={Users}
              title="No recipients yet"
              body="Add a family member in Haiti so you can buy for them. You'll need their phone number and city."
              action={<Button onClick={() => setAdding(true)}><Plus className="w-3.5 h-3.5 mr-1" /> Add first recipient</Button>}
            />
          </Card>
        )}

        {(q.data?.length ?? 0) > 0 && (
          <Card padding="p-0">
            <div className="p-5 border-b border-[var(--color-hairline)]">
              <SectionHeader title={`${q.data!.length} ${q.data!.length === 1 ? 'recipient' : 'recipients'}`} />
            </div>
            <div className="divide-y divide-[var(--color-hairline)]">
              {q.data!.map((r) => (
                <div key={r.id} className="px-5 py-4 flex items-center gap-3">
                  <Link to={`/customer/recipients/${r.id}`} className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-[var(--color-danger-bg)] text-[var(--color-danger-fg)] text-[12px] font-semibold shrink-0 hover:bg-[var(--color-danger-bg)]">
                    {initialsOf(r.full_name)}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/customer/recipients/${r.id}`} className="text-[14px] font-semibold text-[var(--color-ink)] truncate hover:underline block">{r.full_name}</Link>
                    <p className="text-[11.5px] text-[var(--color-secondary-2)] inline-flex items-center gap-3 truncate">
                      <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.city?.name ?? '—'}</span>
                      <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {r.phone_e164}</span>
                      {r.relationship && <span className="capitalize text-[var(--color-muted)]">· {r.relationship}</span>}
                    </p>
                    {r.address_line && <p className="text-[11px] text-[var(--color-muted)] mt-0.5">{r.address_line}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Link to={`/customer/recipients/${r.id}`} className="text-[11.5px] font-semibold text-[var(--color-body)] hover:underline px-2 py-1.5 rounded hover:bg-[var(--color-panel)]">View</Link>
                    <button onClick={() => inviteMut.mutate(r.id)} disabled={inviteMut.isPending} title="Send a WhatsApp invite so they can track orders + confirm receipt" className="text-[11.5px] font-semibold text-[var(--color-success-fg)] hover:underline px-2 py-1.5 rounded hover:bg-[var(--color-success-bg)] inline-flex items-center gap-1 disabled:opacity-50">
                      <Send className="w-3.5 h-3.5" /> Invite
                    </button>
                    <Link to="/customer/requests/new" className="text-[11.5px] font-semibold text-brand-600 hover:underline px-3 py-1.5 rounded hover:bg-brand-50">Buy for them</Link>
                    <button
                      onClick={() => setEditing(r)}
                      className="p-1.5 rounded hover:bg-[var(--color-panel)] text-[var(--color-secondary-2)]"
                      title="Edit"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Remove ${r.full_name}? Active requests must be cancelled first.`)) {
                          removeMut.mutate(r.id);
                        }
                      }}
                      className="p-1.5 rounded hover:bg-[var(--color-danger-bg)] text-[var(--color-secondary-2)] hover:text-[var(--color-danger-fg)]"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {(adding || editing) && (
        <RecipientEditor
          open
          onClose={() => { setAdding(false); setEditing(null); }}
          existing={editing}
        />
      )}
    </PortalShell>
  );
}

function initialsOf(n: string) {
  return n.split(' ').filter(Boolean).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('');
}

// ── Add/Edit modal ────────────────────────────────────────

function RecipientEditor({
  open, onClose, existing,
}: { open: boolean; onClose: () => void; existing: Recipient | null }) {
  const qc = useQueryClient();
  const isEdit = !!existing;

  const citiesQ = useQuery({ queryKey: ['catalog', 'cities'], queryFn: catalog.cities });

  const [fullName, setFullName] = useState(existing?.full_name ?? '');
  const [phone,    setPhone]    = useState(existing?.phone_e164 ?? '');
  const [email,    setEmail]    = useState(existing?.email ?? '');
  const [cityId,   setCityId]   = useState<number | ''>(existing?.city_id ?? '');
  const [relationship, setRelationship] = useState(existing?.relationship ?? '');
  const [addressLine,  setAddressLine]  = useState(existing?.address_line ?? '');
  const [landmark,     setLandmark]     = useState(existing?.landmark ?? '');
  const [lang,         setLang]         = useState<'en'|'fr'|'ht'>((existing?.preferred_lang as 'en'|'fr'|'ht') ?? 'ht');

  // Default to first city when adding.
  useEffect(() => {
    if (!isEdit && cityId === '' && citiesQ.data && citiesQ.data.length > 0) {
      setCityId(citiesQ.data[0]!.id);
    }
  }, [citiesQ.data, cityId, isEdit]);

  const save = useMutation({
    mutationFn: () => {
      const input: RecipientInput = {
        full_name: fullName.trim(),
        phone_e164: phone.trim(),
        email: email.trim() || null,
        city_id: Number(cityId),
        relationship: relationship.trim() || null,
        address_line: addressLine.trim() || null,
        landmark: landmark.trim() || null,
        preferred_lang: lang,
      };
      return isEdit && existing ? recipients.update(existing.id, input) : recipients.create(input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'recipients'] });
      toast.success(isEdit ? 'Recipient updated' : 'Recipient added — invite sent to them');
      onClose();
    },
    onError: (e: Error) => toast.error(e.message || 'Save failed'),
  });

  const valid = fullName.trim() && phone.trim() && cityId !== '';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? `Edit ${existing!.full_name}` : 'Add a recipient'}
      width="md"
      footer={
        <>
          <Button tone="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!valid || save.isPending}>
            {isEdit ? 'Save changes' : 'Add recipient'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Labeled label="Full name" required>
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} className={inputCls} placeholder="e.g. Maria Petit-Frère" />
        </Labeled>
        <div className="grid grid-cols-2 gap-3">
          <Labeled label="HT phone" required hint="E.164 (+509…)">
            <input value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} placeholder="+50932000000" />
          </Labeled>
          <Labeled label="City" required>
            <select value={cityId} onChange={(e) => setCityId(Number(e.target.value))} className={inputCls}>
              <option value="">Select…</option>
              {(citiesQ.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Labeled>
        </div>
        <Labeled label="Email (optional)" hint="We'll send their invite by WhatsApp + email.">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="name@email.com" />
        </Labeled>
        <Labeled label="Relationship">
          <select value={relationship} onChange={(e) => setRelationship(e.target.value)} className={inputCls}>
            <option value="">Select…</option>
            {['mother', 'father', 'parent', 'spouse', 'son', 'daughter', 'child', 'brother', 'sister', 'sibling', 'grandmother', 'grandfather', 'grandparent', 'aunt', 'uncle', 'cousin', 'niece', 'nephew', 'friend', 'neighbor', 'other'].map((rel) => (
              <option key={rel} value={rel}>{rel[0].toUpperCase() + rel.slice(1)}</option>
            ))}
          </select>
        </Labeled>
        <Labeled label="Address (optional)">
          <input value={addressLine} onChange={(e) => setAddressLine(e.target.value)} className={inputCls} placeholder="Rue Lamarre" />
        </Labeled>
        <Labeled label="Landmark (optional)" hint="Helps the merchant find them.">
          <input value={landmark} onChange={(e) => setLandmark(e.target.value)} className={inputCls} placeholder="Near the pharmacy" />
        </Labeled>
        <Labeled label="Preferred language">
          <select value={lang} onChange={(e) => setLang(e.target.value as 'en'|'fr'|'ht')} className={inputCls}>
            <option value="ht">Kreyòl</option>
            <option value="fr">Français</option>
            <option value="en">English</option>
          </select>
        </Labeled>
      </div>
    </Modal>
  );
}

const inputCls = 'w-full px-3 py-2 text-[14px] bg-white border border-[var(--color-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400';

function Labeled({ label, hint, required, children }: { label: string; hint?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider font-semibold text-[var(--color-secondary-2)]">
        {label}{required && <span className="text-[var(--color-danger-fg)]">*</span>}
        {hint && <span className="text-[11px] text-[var(--color-muted)] normal-case ml-1 font-normal">· {hint}</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
